import { TickerLogo } from '@/components/TickerLogo';
import { formatCurrency, formatPercent, useRequiredParams } from 'utils';

import { PolicyQueryError } from '../PolicyQueryError';
import {
    PolicyTable as Table,
    PolicyTableBody as TableBody,
    PolicyTableCell as TableCell,
    PolicyTableHead as TableHead,
    PolicyTableHeader as TableHeader,
    PolicyTableRow as TableRow,
} from '../PolicyTable';
import { PositionAmount } from '../PositionAmount';
import { usePolicyScope } from '../usePolicyScope';
import { ExclusionType, summarizePolicyExclusions } from './policy-exclusions';
import { usePolicyExclusions } from './usePolicyExclusions';

const badgeClasses: Record<ExclusionType, string> = {
    'Custom Target': 'bg-[#f8ecdf] text-[#835c34]',
    'Do Not Hold': 'bg-[#f7e5e2] text-[#963125]',
    'Do Not Trade': 'bg-[#eeece9] text-[#5c5552]',
};

export const PolicyExclusions = () => {
    const { accountGUID, accountGroupGUID } = useRequiredParams(['accountGUID', 'accountGroupGUID']);
    const scope = usePolicyScope(accountGUID, accountGroupGUID);
    const { accounts } = scope;
    const exclusions = usePolicyExclusions(accounts);
    const loading = scope.status === 'loading' || exclusions.isPending;
    const ready = scope.status === 'ready' && !exclusions.isPending && !exclusions.isError;
    const { rows } = exclusions;
    const { total, portfolioPct, accountPct } = summarizePolicyExclusions(
        rows,
        accounts,
        scope.status === 'ready' ? scope.profile.accounts : [],
        accountGUID,
    );
    const percentage = (value?: number) => (ready && value !== undefined ? formatPercent(value, 1) : '—');
    return (
        <div className="space-y-4.5">
            <div className="grid items-center gap-4 xl:min-h-14 xl:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
                <h2 id="policy-exclusions-title" className="text-cocoa-1000 text-[22px] font-medium">
                    Exclusions
                </h2>
                <dl className="flex flex-wrap items-start gap-8 text-center xl:col-start-2">
                    {[
                        ['Total value', ready ? formatCurrency(total) : '—'],
                        ['% of Portfolio', percentage(portfolioPct)],
                        ['% of Account', percentage(accountPct)],
                    ].map(([label, value]) => (
                        <div key={label}>
                            <dt className="text-cocoa-500 text-[13px]">{label}</dt>
                            <dd className="text-cocoa-1000 mt-1 text-lg font-medium tabular-nums">{value}</dd>
                        </div>
                    ))}
                </dl>
            </div>
            {scope.status !== 'error' && (exclusions.isError || exclusions.isAssetError) && (
                <PolicyQueryError
                    message={
                        exclusions.isError
                            ? 'Some exclusion data could not be loaded. Totals are unavailable.'
                            : 'Some security names could not be loaded. Security IDs are shown instead.'
                    }
                    queries={exclusions.retryQueries}
                />
            )}
            {scope.status === 'error' ? (
                <PolicyQueryError message="Couldn’t load exclusions." queries={scope.retryQueries} />
            ) : (
                <Table
                    className="min-w-[900px]"
                    footer={
                        <>
                            <span className="font-medium">Total</span>
                            <span className="font-medium tabular-nums">{ready ? formatCurrency(total) : '—'}</span>
                        </>
                    }
                >
                    <TableHeader>
                        <TableRow>
                            {['Symbol', 'Description', 'Type', 'Details', 'Account', 'Current Position'].map(
                                (label) => (
                                    <TableHead
                                        key={label}
                                        className={label === 'Current Position' ? 'text-right' : undefined}
                                    >
                                        {label}
                                    </TableHead>
                                ),
                            )}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} className="text-cocoa-500 py-8 text-center" role="status">
                                    {loading
                                        ? 'Loading exclusions…'
                                        : !ready
                                          ? 'Exclusions are currently unavailable.'
                                          : 'No exclusions configured.'}
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((row) => (
                                <TableRow key={row.key}>
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-stone-200 p-1">
                                                <TickerLogo
                                                    tickerSymbol={row.symbol}
                                                    companyName={row.description}
                                                    className="size-full object-contain"
                                                />
                                            </span>
                                            <span className="font-medium">{row.symbol ?? row.securityId}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>{row.description}</TableCell>
                                    <TableCell>
                                        <span
                                            className={`inline-block rounded-[4px] px-2 py-1 text-xs leading-normal font-medium whitespace-nowrap ${badgeClasses[row.type]}`}
                                        >
                                            {row.type}
                                        </span>
                                    </TableCell>
                                    <TableCell className="whitespace-nowrap">{row.details}</TableCell>
                                    <TableCell>{row.accountName}</TableCell>
                                    <TableCell
                                        className={`text-right font-medium tabular-nums ${row.currentExposure === 0 ? 'text-cocoa-400' : ''}`}
                                    >
                                        <PositionAmount
                                            value={row.currentExposure}
                                            status={exclusions.positionsStatus}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            )}
            {exclusions.isPending && rows.length > 0 && (
                <p role="status" className="text-cocoa-500 text-sm">
                    Additional holdings and position amounts are loading. Totals are not yet available.
                </p>
            )}
        </div>
    );
};
